import {AfterViewInit, Component, ElementRef, Injector, ViewChild} from '@angular/core';
import {FormControl} from '@angular/forms';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {ActivatedRoute, Router} from '@angular/router';
import {Observable} from 'rxjs';
import {MasterDetailActionSource, MasterDetailEvent, MasterDetailHub} from '../master-detail/master-detail-hub.service';
import {ButtonInvoke} from '../panel-buttons/panel-buttons.component';
import {CollectionService, Column, ColumnType, FilterType} from './collection.service';
import {FromStringToEnumPipe} from "../pipes/fromStringToEnum.pipe";

@Component({
    selector: 'cml-collection',
    templateUrl: './collection.component.html',
    styleUrls: ['./collection.component.scss'],
    standalone: false
})
export class CollectionComponent implements AfterViewInit {

  @ViewChild(MatPaginator)
    paginator!: MatPaginator;

  @ViewChild(MatSort)
    sort!: MatSort;

  @ViewChild('filterInput')
  filterInputElement!: ElementRef;

  isLoading$: Observable<boolean>;

  public get FilterType() {
    return FilterType;
  }


  public get ColumnType() {
    return ColumnType;
  }

  dataSource = new MatTableDataSource();
  visibleColumns: Column[];
  visibleFields: any;
  filterable: FilterType;

  buttons: ButtonInvoke[] = [];

  filterFormGroup: any;

  resultsLength = 0;

  private collectionService: CollectionService | any;
  private basePath: string = "";
  private filterValues: any = {};

  private onDetailRefreshSubscription: any;

  constructor(
    public route: ActivatedRoute,
    private router: Router,
    private injector: Injector,
    private masterDetailHub: MasterDetailHub,
    private stringToEnumPipe: FromStringToEnumPipe) {
    let routeConfig = route.routeConfig;

    if (routeConfig && routeConfig.data) {
      this.collectionService = this.injector.get(routeConfig.data['collectionService']);
      this.collectionService.component = this;
      this.basePath = routeConfig.data["basePath"];
    }
    this.visibleColumns = this.collectionService.settings.columns;
    this.visibleFields = this.collectionService.settings.fields;
    this.filterable = this.collectionService.settings.filterable;
    this.isLoading$ = this.collectionService.baseCrudService.isLoading$;
    this.filterFormGroup = this.createFilterGroup();
    this.onDetailRefreshSubscription = this.masterDetailHub.listener.subscribe((event: MasterDetailEvent) => {
      if (event.source === MasterDetailActionSource.detail) {
        this.reload();
      }
    });
  }

  ngAfterViewInit() {
    this.buttons = this.collectionService.createToolbarButtons();
    this.paginator.page.subscribe(() => this.loadPage());
    this.paginator._intl.itemsPerPageLabel = 'Záznamů na stránku';
    this.paginator._intl.nextPageLabel = 'Následující strana';
    this.paginator._intl.previousPageLabel = 'Předchozí strana';
    this.dataSource.filterPredicate = this.createFilter();
    this.dataSource.sort = this.sort;
    if(this.filterable === FilterType.FRONTEND){
      this.dataSource.paginator = this.paginator;
      this.dataSource.sortingDataAccessor = this.sortingAccessor();
    }
    this.loadPage();
  }

  ngOnDestroy() {
    this.onDetailRefreshSubscription.unsubscribe();
  }

  async reload() {
    await this.loadPage();
    this.masterDetailHub.emitRefresh(MasterDetailActionSource.master);
  }

  async invoke(method: Exclude<keyof CollectionComponent, "invoke">) {
    await this[method]();
  }

  async loadPage() {
    let skipCount = this.paginator.pageIndex * this.paginator.pageSize;
    let result = await this.collectionService.getList({ skipCount: skipCount, maxResultCount: this.paginator.pageSize });
    this.dataSource.data = result.items;
    this.resultsLength = result.totalCount;
  }

  createFilterGroup() {
    const group: any = {};
    this.visibleColumns.forEach(item => {
      group[item.name] = new FormControl([null]);
    });
    return group;
  }

  getFilterGroup(column: Column): any {
    return this.filterFormGroup[column.name];
  }

  public getWidthOfFilter(column: Column) : Object
  {
    if(column.width)
    {
      const currentWidth = column.width - 20;//20 je konstantni sirka sortovaci sipky
      return {'width' :  currentWidth + 'px', 'max-width' : currentWidth + 'px'}
    }
    return {'width' :'100%'};
  }

  public getCellWidth(column: Column): any
  {
    if(column.width)
    {
      return {'width' :  column.width + 'px', 'max-width' : column.width + 'px'}
    }
    return {}
  }

  applyFilter(event: any, column: Column) {
    const value = (event.target as HTMLInputElement).value;
    if(column.enumType != null){
      this.handleEnumColumns(value, column);
      return;
    }
    this.filterValues[column.name] = (event.target as HTMLInputElement).value;
    if (this.filterable === FilterType.FRONTEND) {
      Object.keys(this.filterValues).forEach(x => {
        this.filterValues[x] = this.filterValues[x] = typeof this.filterValues[x] == 'string' ? this.filterValues[x].toLowerCase() : this.filterValues[x];
      });
      this.dataSource.filter = JSON.stringify(this.filterValues);
    }
    else {
      //todo backend filtering
    }
  }

  private handleEnumColumns(value: string, column: Column): void{
    const transformedValueForEnum = value == "" ? "" : this.stringToEnumPipe.transform(value.toUpperCase(), column.enumType);
    this.filterValues[column.name] = column.enumType != null ? transformedValueForEnum == null ? -1 : transformedValueForEnum : value;
    this.dataSource.filter = JSON.stringify(this.filterValues);
  }

  private createFilter(): (data: any, filter: string) => boolean {
    return (data, filter): boolean => {
      let searchTerms = JSON.parse(filter);
      let columns = Object.keys(searchTerms);
      let filtered: boolean = true;
      for (let column of columns) {
        if (!filtered) {
          return filtered;
        }

        let value = searchTerms[column];
        if (value !== null && value !== "") {
          let cellValue = this.getCellValue(data, column);
          if (cellValue == null) {
            filtered = false;
          } else {
            let columnType = this.visibleColumns.find(x => x.name === column)?.type;
            if (columnType === ColumnType.BOOLEAN) {
              filtered = (cellValue ? 'ano' : 'ne').indexOf(value) !== -1;
            } else if (columnType === ColumnType.ENUM) {
              if (typeof value === "number") {
                filtered = cellValue === value;
              }
              if (Array.isArray(value)) {
                filtered = value.includes(cellValue);
              }
            } else {
              filtered = cellValue.toString().toLowerCase().indexOf(value) !== -1;
            }
          }
        } else {
          filtered = true;
        }
      }
      return filtered;
    };
  }

  private sortingAccessor(): (data: any, sortHeaderId: string) => string {
    return (data, sortHeaderId): string => {
      if (typeof data[sortHeaderId] === 'string') {
        return data[sortHeaderId].toLocaleLowerCase();
      }
      return data[sortHeaderId];
    };
  }

  markRow(row: any): boolean
  {
    let keys = this.collectionService.getArrayOfKeys();
    let rowKey = keys.map((x:string) => row[x]?.toString()?.toLowerCase()).join('/');
    if(!rowKey){
      return false;
    }
    const regex = new RegExp(`\\b${rowKey}\\b`);

    return  regex.test(this.router.url)
    //return this.router.url.includes(rowKey);
  }
  async selectValue(row: any)
  {
    // zatim je tu navigace
    await this.navigate(row);
  }

  getCellValue(element: any, column: string) {
    const parts = column.split('.');
    let value = element;
    for (let part of parts) {
      value = value[part];
    }
    return value;
  }

  async navigate(row: any)
  {
    this.collectionService.navigate(this.router, this.basePath, row);
  }

  create() {
    this.collectionService.navigateToCreate(this.router, this.basePath);
  }


}
